import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Chip from '../../../components/chip/chip.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';
import StructureService from '../../../services/structure-service';
import { SEARCH_RESULT_VIEW } from '../../../constants/entity/permissions';

const AssetPropertyInformation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    assetData,
    selectedAssetTypes,
    selectedTags,
    assetTypeList,
    measureUnitList,
    tagList,
    onAssetTypesChange,
    onTagClick,
    onTagsChange,
    onCancelForm,
    onSubmitForm,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const formPrefix: string = 'assetData';

  const measureUnitMap: Record<string, MeasureUnit> = useMemo((): Record<string, MeasureUnit> => (
    StructureService.getItemMap(measureUnitList, 'name')
  ), [measureUnitList]);

  const assetSizeMeasureUnit: string = assetData && assetData.sizeType && assetData.sizeType.name && measureUnitMap
    && measureUnitMap[assetData.sizeType.name] && (measureUnitMap[assetData.sizeType.name].title || '');
  const groundLeaseLabel: string = assetData && FormatService.formatBooleanValue(assetData.groundLease, true);
  const isEmpty: boolean = !isEditMode && !assetData.lotSize && !assetData.affordableUnits && (!assetData.lastDeal
      || (assetData.lastDeal && !assetData.lastDeal.zfaAmount && !assetData.lastDeal.gsfAmount
        && !assetData.lastDeal.nsfAmount && !assetData.lastDeal.unitsAmount && !assetData.lastDeal.keysAmount
        && !assetData.lastDeal.acresAmount && !assetData.lastDeal.lotsfAmount)) && !assetData.leedDesignation
    && !assetData.yearBuilt && !assetData.yearRenovated && !assetData.primaryCollateral && !groundLeaseLabel
    && (!assetData.types || !!Object.keys(assetData.types).length);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isEmpty={isEmpty}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      {
        (!isEmpty || isEditMode) && (
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              {
                (isEditMode || assetData.size) && (
                  <TableRow>
                    <TableCell>
                      Size
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FieldSeparatedBlock withOffset>
                            <FormFieldNumber isBordered name={`${formPrefix}.size`} />
                            <FormFieldSelect
                              isBordered
                              withBlankElement
                              isDisabled={measureUnitList && !measureUnitList.length}
                              name={`${formPrefix}.sizeType`}
                              optionsTotal={measureUnitList && measureUnitList.length}
                            >
                              {menuItemMeasureUnitList}
                            </FormFieldSelect>
                          </FieldSeparatedBlock>
                        ) : (
                          assetData.size && `${assetData.size} ${assetSizeMeasureUnit}`
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.lotSize) && (
                  <TableRow>
                    <TableCell>
                      Lot Size
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.lotSize`} />
                        ) : assetData.lotSize
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.affordableUnits) && (
                  <TableRow>
                    <TableCell>
                      Affordable Units
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.affordableUnits`} />
                        ) : assetData.affordableUnits
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.zfaAmount && (
                  <TableRow>
                    <TableCell>
                      ZFA
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.zfaAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.gsfAmount && (
                  <TableRow>
                    <TableCell>
                      GSF
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.gsfAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.nsfAmount && (
                  <TableRow>
                    <TableCell>
                      GSF
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.nsfAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.unitsAmount && (
                  <TableRow>
                    <TableCell>
                      Units
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.unitsAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.keysAmount && (
                  <TableRow>
                    <TableCell>
                      Keys
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.keysAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.acresAmount && (
                  <TableRow>
                    <TableCell>
                      Acres
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.acresAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.lotsfAmount && (
                  <TableRow>
                    <TableCell>
                      Lot SF
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.lotsfAmount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.leedDesignation) && (
                  <TableRow>
                    <TableCell>
                      LEED Designation
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.leedDesignation`} />
                        ) : assetData.leedDesignation
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.yearBuilt) && (
                  <TableRow>
                    <TableCell>
                      Year Built
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.yearBuilt`} />
                        ) : assetData.yearBuilt
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.yearRenovated) && (
                  <TableRow>
                    <TableCell>
                      Year Renovated
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.yearRenovated`} />
                        ) : assetData.yearRenovated
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.primaryCollateral) && (
                  <TableRow>
                    <TableCell>
                      Primary Collateral
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldText isBordered name={`${formPrefix}.primaryCollateral`} />
                        ) : assetData.primaryCollateral
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || !!groundLeaseLabel) && (
                  <TableRow>
                    <TableCell>
                      Ground Lease
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldCheckbox name={`${formPrefix}.groundLease`} />
                        ) : (groundLeaseLabel || null)
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || (assetData.types && !!Object.keys(assetData.types).length)) && (
                  <TableRow>
                    <TableCell>
                      Asset Classes
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldMultiSelect
                            isBordered
                            isDisabled={assetTypeList && !assetTypeList.length}
                            keyField="name"
                            name={`${formPrefix}.types`}
                            optionList={assetTypeList}
                            onValuesChange={onAssetTypesChange}
                          >
                            {menuItemAssetTypeList}
                          </FormFieldMultiSelect>
                        ) : (
                          assetData.types && !!Object.keys(assetData.types).length
                          && assetData.types.map((assetTypeItem: AssetType): ReactElement => (
                            <Chip isDisabled key={assetTypeItem.id} label={assetTypeItem.title} />
                          ))
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || (assetData.tags && !!Object.keys(assetData.tags).length)) && (
                  <TableRow>
                    <TableCell>
                      Tags
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldMultiSelect
                            isBordered
                            isAbleToAdd
                            withSearch
                            isDisabled={tagList && !tagList.length}
                            keyField="name"
                            name={`${formPrefix}.tags`}
                            optionList={tagList}
                            onSearch={onTagSearch}
                            onClose={onTagSearchClose}
                            onValuesChange={onTagsChange}
                          >
                            {menuItemTagList}
                          </FormFieldMultiSelect>
                        ) : (
                          assetData.tags && !!Object.keys(assetData.tags).length
                          && assetData.tags.map((tagItem: Tag): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={tagItem.id}
                              label={tagItem.name}
                              permission={SEARCH_RESULT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onTagClick(event, tagItem.id);
                              }}
                            />
                          ))
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        )
      }
    </SinglePageTab>
  );
});

export default AssetPropertyInformation;
